import { generateNumericId } from '@/lib/utils';
import { DEFAULT_ORGANIZATION, DEFAULT_CLUB, SAMPLE_DATA_CONFIG } from './config';
import type { Organization, Club, User, Space, UserRoleType } from '@/lib/types';

/**
 * Sample Data Generation Utilities
 * These functions help create consistent sample data that mimics real data structures
 * without relying on hardcoded IDs or specific entities.
 */

export function createSampleOrganization(overrides?: Partial<Organization>): Organization {
  return {
    ...DEFAULT_ORGANIZATION,
    id: overrides?.id || `org_${generateNumericId(10)}`,
    name: overrides?.name || 'Sample Organization',
    status: 'Active',
    currencyCode: 'USD',
    tenantId: overrides?.id || `org_${generateNumericId(10)}`,
    ...overrides,
  };
}

export function createSampleClub(overrides?: Partial<Club>): Club {
  return {
    ...DEFAULT_CLUB,
    id: overrides?.id || `club_${generateNumericId(10)}`,
    name: overrides?.name || 'Sample Club',
    description: 'A sample club for testing',
    status: 'Active',
    sportsOffered: ['Tennis'],
    currencyCode: 'USD',
    organizationId: SAMPLE_DATA_CONFIG.defaultOrganizationId,
    tenantId: SAMPLE_DATA_CONFIG.defaultOrganizationId,
    ...overrides,
  };
}

export function createSampleUser(overrides?: Partial<User>): User {
  const uid = overrides?.uid || `user_${generateNumericId(10)}`;
  const firstName = overrides?.firstName || 'Sample';
  const lastName = overrides?.lastName || 'User';
  const email = overrides?.email || `user_${generateNumericId(5)}@example.com`;
  const roles = overrides?.roles || ['Member'] as UserRoleType[];
  const organizationId = overrides?.organizationId || SAMPLE_DATA_CONFIG.defaultOrganizationId;
  const tenantId = overrides?.tenantId || SAMPLE_DATA_CONFIG.defaultOrganizationId;
  const now = new Date().toISOString();
  
  return {
    // Firebase Auth standard properties
    uid,
    email,
    emailVerified: true,
    displayName: `${firstName} ${lastName}`,
    photoURL: '/user.png',
    phoneNumber: `+1${Math.floor(Math.random() * 9000000000 + 1000000000)}`,
    disabled: false,
    
    // Custom user properties
    firstName,
    lastName,
    roles,
    status: 'Active',
    
    // Authentication metadata
    creationTime: now,
    lastSignInTime: now,
    dateUpdated: now,
    
    // Tenant context
    organizationId,
    tenantId,
    
    // Business logic properties
    associatedClubIds: overrides?.associatedClubIds || [],
    canBookAllDay: overrides?.canBookAllDay || false,
    platformPlanId: overrides?.platformPlanId,
    
    // Provider data
    providerData: [{
      uid,
      displayName: `${firstName} ${lastName}`,
      email,
      phoneNumber: `+1${Math.floor(Math.random() * 9000000000 + 1000000000)}`,
      photoURL: '/user.png',
      providerId: 'password'
    }],
    
    // Custom claims for Firebase Auth
    customClaims: {
      roles,
      tenantId,
      organizationId,
      permissions: overrides?.customClaims?.permissions || []
    },
    
    ...overrides,
  };
}

export function createSampleSpace(overrides?: Partial<Space>): Space {
  return {
    id: overrides?.id || `space_${generateNumericId(10)}`,
    businessId: overrides?.businessId || 'SAMPLE_SPACE',
    displayName: overrides?.displayName || 'Sample Space',
    description: 'A sample space for testing',
    spaceType: 'Court',
    facilityType: 'Outdoor',
    hasLights: false,
    minCapacity: 2,
    maxCapacity: 4,
    status: 'active',
    availability: [],
    organizationId: SAMPLE_DATA_CONFIG.defaultOrganizationId,
    clubId: SAMPLE_DATA_CONFIG.defaultClubId,
    tenantId: SAMPLE_DATA_CONFIG.defaultOrganizationId,
    ...overrides,
  };
} 
